import { Alert, AlertIcon, Box, Button, Card, CardBody, Divider, Flex, FormControl, FormLabel, Input, NumberInput, NumberInputField, Switch, Text, VStack } from "@chakra-ui/react";
import { MdAdd, MdRemove } from 'react-icons/md';
import { clamp } from "date-fns";
import { v4 } from "uuid";
import { translate } from "../../../i18n";
// Direct module import, not the `states` barrel — see the note at the same
// import in CreateAdminBookingModal.tsx: the barrel drags Chakra's `forwardRef`
// in through SkeletonCardGrid and breaks any suite that mocks Chakra.
import InlineErrorState from "../../../components/states/InlineErrorState";
import { Currency } from "../../../models/court-pricing/dto/court-pricing-rule.dto";
import { DayOfWeek } from "../../../models/court/dto/batch-upsert-availabilities.dto";
import { CreateCourtFormAvailabilityInputType, SLOTS_PER_HOUR } from "../../../models/court/dto/court-availability-template.constants";
import { SportType } from "../../../models/court/dto/court.dto";
import { getDefaultCurrency } from "../../court-pricing-management/components/CurrencyPicker";
import DurationInput from "../../court-pricing-management/components/DurationInput";
import PriceInput from "../../court-pricing-management/components/PriceInput";
import AvailabilitiesHeatmap from "./AvailabilitiesHeatmap";
import { numberToHourMinuteLabel } from "./CreateCourtForm";
import MultiDayOfWeekPicker from "./MultiDayOfWeekPicker";
import RangePicker from "./RangePicker";
import { typography } from 'theme/tokens';
import SportPicker from "./SportPicker";
import { parsePrice } from "../../../utils/parse-price";
import { MAX_BOOKING_CAPACITY, MIN_BOOKING_CAPACITY, capacityFieldValue } from "../../../utils/sport-capacity";
import { buildCapacityOfferingLookups, findAvailabilityCapacityIssues, findCapacityOfferingMismatches, findSlotsWithoutCapacityFallback } from "../utils/validate-availability-prices";
import { capacityIssueMessage } from "../utils/capacity-issue-message";
import { useGetOrganizationGameCapacityOptions } from "../../organization/hooks/useOrganizationGameCapacityOptions";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { useMemo } from "react";

const CAPACITY_BOUNDS = {
    minCapacity: MIN_BOOKING_CAPACITY,
    maxCapacity: MAX_BOOKING_CAPACITY,
};

/**
 * The capacity problems in ONE availability block, as operator-facing lines.
 *
 * Rendered next to the block that owns them so the operator fixes the right
 * row: the API rejects the whole batch for a duplicate and gives no hint which
 * of a year's slots caused it.
 */
export function describeCapacityIssues(
    configurations: { sport?: unknown; slotDurationInMs?: unknown; capacity?: number }[],
    capacityOffering: {
        bookableCapacitiesForSport?: (sport: unknown) => number[];
        offeredCapacitiesForSport?: (sport: unknown) => number[];
    } = {},
): { status: 'error' | 'warning'; message: string }[] {
    const lines: { status: 'error' | 'warning'; message: string }[] = [];
    const seen = new Set<string>();

    const push = (status: 'error' | 'warning', message: string) => {
        if (seen.has(message)) return;
        seen.add(message);
        lines.push({ status, message });
    };

    for (const issue of findAvailabilityCapacityIssues([configurations], CAPACITY_BOUNDS)) {
        push('error', capacityIssueMessage(issue));
    }

    // Only capacities a booking can actually be made at, so the correct
    // two-price configuration (every offered capacity priced, no wildcard row)
    // does not warn — it used to, on every save, which is how an operator
    // learns to ignore the box.
    for (const gap of findSlotsWithoutCapacityFallback([configurations], capacityOffering)) {
        push(
            'warning',
            translate('courts.capacity_without_fallback')
                .replace('{{sport}}', String(gap.sport))
                // Global: the sentence names the capacities twice — what is
                // missing, and what to add.
                .replace(/\{\{unpriced\}\}/g, gap.unpriced.join(', ')),
        );
    }

    // The cross-check against what the organization actually offers. Both
    // shapes are saveable and both are invisible from this screen — one makes
    // the price unbuyable, the other quietly hands membership holders a bigger
    // discount at the smaller capacities.
    for (const mismatch of findCapacityOfferingMismatches([configurations], capacityOffering)) {
        push(
            'warning',
            mismatch.kind === 'not-bookable'
                ? translate('courts.capacity_not_offered')
                      .replace('{{capacity}}', String(mismatch.capacity))
                      .replace('{{sport}}', String(mismatch.sport))
                      .replace('{{bookable}}', mismatch.bookable.join(', '))
                : translate('courts.capacity_uniform_price')
                      .replace('{{sport}}', String(mismatch.sport))
                      .replace('{{capacities}}', mismatch.capacities.join(', ')),
        );
    }

    return lines;
}

type Props = {
    returnAsOneChunk: boolean;
    setReturnAsOneChunk: (returnAsOneChunk: boolean) => void;
    startDate: Date;
    setStartDate: (date: Date) => void;
    endDate: Date;
    setEndDate: (date: Date) => void;
    onAddAvailability: () => void;
    onRemoveAvailability: () => void;
    availabilities: CreateCourtFormAvailabilityInputType[];
    onAvailabilitiesChange: (newAvailabilities: CreateCourtFormAvailabilityInputType[]) => void;
};

export type ExtendedAvailabilityType = CreateCourtFormAvailabilityInputType & {
    sport?: SportType;
    price?: number;
    currency?: string;
};

const formatDateForInput = (date: Date): string => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export default function AddAvailabilitiesForm({ returnAsOneChunk, setReturnAsOneChunk, startDate, endDate, setStartDate, setEndDate, availabilities, onAddAvailability, onAvailabilitiesChange }: Props) {
    // What the organization offers lives on a different screen entirely (Game
    // capacity management), and until now neither screen could see the other:
    // pricing a capacity nobody offers made the slot unbookable, and offering
    // capacities nobody priced made a percentage discount vary with the
    // player's pick. The hook is org-scoped and its own cache handles repeats.
    const { selectedOrganization } = useSelectedOrganization();
    // `isError` is taken because `buildCapacityOfferingLookups` returns `{}` for
    // `undefined` — which switches the whole cross-check OFF rather than
    // reporting every capacity-specific row as unbookable while a request is in
    // flight. That is right for "in flight" and wrong for "refused" (R95-2): the
    // read is behind `AdminAuthGuard` + `hasOrganizationAdminAccess`, so a stale
    // token, a member-not-admin operator or any 5xx silently disarms the two
    // warnings this form exists to raise, and the operator saves a year of
    // availabilities believing they were checked against what the organization
    // sells. A check whose failure branch proceeds without the check reads
    // exactly like a check.
    const { data: capacityOptions, isError: isCapacityOptionsError } =
        useGetOrganizationGameCapacityOptions(selectedOrganization?.id ?? '');
    const capacityOffering = useMemo(
        () => buildCapacityOfferingLookups(capacityOptions),
        [capacityOptions],
    );

    return (
        <VStack spacing={5} align="stretch">
            {/* Says the cross-check could not run, rather than letting its
                silence read as "nothing is wrong". Nothing is withheld: the
                form's own bounds checks (`findAvailabilityCapacityIssues`) do
                not depend on this read and still fire. */}
            {isCapacityOptionsError && (
                <InlineErrorState
                    message={translate('game_capacity_options.error_loading')}
                />
            )}
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel {...typography.formLabel} mb={0}>{translate('courts.return_as_one_chunk')}</FormLabel>
                <Switch
                    isChecked={returnAsOneChunk}
                    size="md"
                    colorScheme="brandScheme"
                    onChange={(e) => setReturnAsOneChunk(e.target.checked)}
                />
            </FormControl>

            <Flex gap={4}>
                <FormControl flex={1}>
                    <FormLabel {...typography.formLabel}>{translate('event.start_date')}</FormLabel>
                    <Input
                        type="date"
                        value={formatDateForInput(startDate)}
                        onChange={(e) => {
                            const newStartDate = new Date(e.target.value);
                            newStartDate.setHours(0, 0, 0, 0);
                            setStartDate(newStartDate);
                        }}
                    />
                </FormControl>
                <FormControl flex={1}>
                    <FormLabel {...typography.formLabel}>{translate('event.end_date')}</FormLabel>
                    <Input
                        type="date"
                        max={new Date(new Date().getTime() + 364 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                        value={formatDateForInput(endDate)}
                        onChange={(e) => {
                            const newEndDate = new Date(e.target.value);
                            newEndDate.setHours(23, 59, 59, 999);
                            setEndDate(newEndDate);
                        }}
                    />
                </FormControl>
            </Flex>

            {availabilities.map((availability, availabilityIndex) => (
                <Card key={availabilityIndex} variant="outline">
                    <CardBody>
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <FormLabel {...typography.formLabel} mb={0}>{translate('courts.availabilities')}</FormLabel>
                                    <Button
                                        colorScheme="red"
                                        variant="ghost"
                                        size="sm"
                                        leftIcon={<MdRemove />}
                                        onClick={() => onAvailabilitiesChange(availabilities.filter((_, i) => i !== availabilityIndex))}
                                        isDisabled={availabilities.length <= 1}
                                        aria-label={translate('delete')}
                                    >
                                        {translate('delete')}
                                    </Button>
                                </Flex>
                                <Flex align="center" gap={4}>
                                    <MultiDayOfWeekPicker
                                            buttonWidth={'450px'}
                                            value={availability.daysOfWeek}
                                            onChange={newValue => {
                                                onAvailabilitiesChange(availabilities.map((a, i) => {
                                                    if (i === availabilityIndex) {
                                                        return { ...a, daysOfWeek: newValue };
                                                    }
                                                    return a;
                                                }))
                                            }}
                                        />
                                        <Text fontSize="sm" fontWeight="600" minW="50px" textAlign="center">
                                        {numberToHourMinuteLabel(availability.min, SLOTS_PER_HOUR)}
                                    </Text>
                                    <RangePicker
                                            labelsEveryXStep={4}
                                            step={1}
                                            defaultValue={[availability.min, availability.max]}
                                            max={24 * SLOTS_PER_HOUR}
                                            min={0}
                                            onChange={newValues => {
                                                onAvailabilitiesChange(availabilities.map((a, i) => {
                                                    if (i === availabilityIndex) {
                                                        return { ...a, min: newValues[0], max: newValues[1] };
                                                    }
                                                    return a;
                                                }));
                                            }}
                                        />
                                    <Text fontSize="sm" fontWeight="600" minW="50px" textAlign="center">
                                        {numberToHourMinuteLabel(availability.max, SLOTS_PER_HOUR)}
                                    </Text>
                                </Flex>
                                </Box>
                                <Divider />
                                {availability.configurations.map((config, configIndex) => (
                                    <Box key={config.id}>
                                        <Flex gap={4} align="flex-start">
                                            <FormControl>
                                                <FormLabel {...typography.formLabel}>{translate('event.sport')}</FormLabel>
                                                <SportPicker
                                                    sport={config.sport || SportType.PADEL}
                                                    setSport={(newSport) => {
                                                        onAvailabilitiesChange(availabilities.map((a, i) => {
                                                            if (i === availabilityIndex) {
                                                                const newConfigs = [...a.configurations];
                                                                newConfigs[configIndex] = { ...newConfigs[configIndex], sport: newSport[0] };
                                                                return { ...a, configurations: newConfigs };
                                                            }
                                                            return a;
                                                        }))
                                                    }}
                                                />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel {...typography.formLabel}>{translate('courts.slot_duration')}</FormLabel>
                                                <DurationInput
                                                    durationInMs={config.slotDurationInMs}
                                                    setDuration={newDuration => {
                                                        onAvailabilitiesChange(availabilities.map((a, i) => {
                                                            if (i === availabilityIndex) {
                                                                const newConfigs = [...a.configurations];
                                                                newConfigs[configIndex] = { ...newConfigs[configIndex], slotDurationInMs: newDuration };
                                                                return { ...a, configurations: newConfigs };
                                                            }
                                                            return a;
                                                        }))
                                                    }}
                                                />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel {...typography.formLabel}>{translate('courts.price')}</FormLabel>
                                                <PriceInput
                                                    name="court_pricing"
                                                    value={config.priceRaw ?? config.price ?? ''}
                                                    onChange={(event) => {
                                                        const raw = event.target.value;
                                                        onAvailabilitiesChange(availabilities.map((a, i) => {
                                                            if (i === availabilityIndex) {
                                                                const newConfigs = [...a.configurations];
                                                                const parsed = parsePrice(raw);
                                                                newConfigs[configIndex] = { ...newConfigs[configIndex], priceRaw: raw, price: isNaN(parsed) ? undefined : parsed };
                                                                return { ...a, configurations: newConfigs };
                                                            }
                                                            return a;
                                                        }))
                                                    }}
                                                    onBlur={() => {
                                                        onAvailabilitiesChange(availabilities.map((a, i) => {
                                                            if (i === availabilityIndex) {
                                                                const newConfigs = [...a.configurations];
                                                                newConfigs[configIndex] = { ...newConfigs[configIndex], priceRaw: undefined };
                                                                return { ...a, configurations: newConfigs };
                                                            }
                                                            return a;
                                                        }))
                                                    }}
                                                    currency={config.currency || getDefaultCurrency()}
                                                    setCurrency={(newCurrency) => {
                                                        onAvailabilitiesChange(availabilities.map((a, i) => {
                                                            if (i === availabilityIndex) {
                                                                const newConfigs = [...a.configurations];
                                                                newConfigs[configIndex] = { ...newConfigs[configIndex], currency: newCurrency };
                                                                return { ...a, configurations: newConfigs };
                                                            }
                                                            return a;
                                                        }))
                                                    }}
                                                />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel {...typography.formLabel}>{translate('game_capacity_options.capacity')}</FormLabel>
                                                <NumberInput
                                                    value={capacityFieldValue(config.capacity)}
                                                    onChange={(valueString, valueAsNumber) => {
                                                        onAvailabilitiesChange(availabilities.map((a, i) => {
                                                            if (i === availabilityIndex) {
                                                                const newConfigs = [...a.configurations];
                                                                newConfigs[configIndex] = {
                                                                    ...newConfigs[configIndex],
                                                                    capacity: valueString === '' ? undefined : valueAsNumber
                                                                };
                                                                return { ...a, configurations: newConfigs };
                                                            }
                                                            return a;
                                                        }))
                                                    }}
                                                    min={MIN_BOOKING_CAPACITY}
                                                    max={MAX_BOOKING_CAPACITY}
                                                    size="sm"
                                                >
                                                    <NumberInputField placeholder={translate('courts.all_capacities')} />
                                                </NumberInput>
                                            </FormControl>
                                            <Button
                                                colorScheme="red"
                                                variant="ghost"
                                                size="sm"
                                                mt={7}
                                                onClick={() => {
                                                    onAvailabilitiesChange(availabilities.map((a, i) => {
                                                        if (i === availabilityIndex) {
                                                            return {
                                                                ...a,
                                                                configurations: a.configurations.filter((_, ci) => ci !== configIndex)
                                                            };
                                                        }
                                                        return a;
                                                    }))
                                                }}
                                                isDisabled={availability.configurations.length <= 1}
                                                aria-label={translate('delete')}
                                            >
                                                <MdRemove />
                                            </Button>
                                        </Flex>
                                        {configIndex < availability.configurations.length - 1 && <Divider my={2} />}
                                    </Box>
                                ))}
                                {describeCapacityIssues(availability.configurations, capacityOffering).map(line => (
                                    <Alert key={line.message} status={line.status} borderRadius="md" fontSize="sm">
                                        <AlertIcon />
                                        {line.message}
                                    </Alert>
                                ))}
                                <Box>
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            onAvailabilitiesChange(availabilities.map((a, i) => {
                                                if (i === availabilityIndex) {
                                                    return {
                                                        ...a,
                                                        configurations: [...a.configurations, {
                                                            id: v4(),
                                                            slotDurationInMs: 60 * 60 * 1000,
                                                            sport: SportType.PADEL,
                                                            price: 0,
                                                            currency: getDefaultCurrency()
                                                        }]
                                                    };
                                                }
                                                return a;
                                            }))
                                        }}
                                    >
                                        {translate('add')}
                                    </Button>
                                </Box>
                            </VStack>
                        </CardBody>
                    </Card>
                ))}
            <Flex gap={3}>
                <Button colorScheme="brand" size="sm" leftIcon={<MdAdd />} onClick={onAddAvailability}>
                    {translate('add')}
                </Button>
            </Flex>

            <AvailabilitiesHeatmap availabilities={availabilities} />
        </VStack>
    )
}

export function getDatesInRange(startDate: Date, endDate: Date, timeSchedule: {
    dayOfWeek: DayOfWeek;
    startHour: number;
    startMin: number;
    endHour: number;
    endMin: number;
    pricings: { id: string; price: number; slotDurationInMs: number; sport: SportType; currency: Currency; capacity: number; }[];
}[]): [Date, Date, { id: string; price: number; slotDurationInMs: number; sport: SportType; currency: Currency; capacity: number; }[]][] {
    const dates: [Date, Date, { id: string; price: number; slotDurationInMs: number; sport: SportType; currency: Currency; capacity: number; }[]][] = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();
        const matchingSchedules = timeSchedule.filter(schedule => schedule.dayOfWeek === dayOfWeek);

        matchingSchedules.forEach(schedule => {
            const startDateTime = new Date(currentDate);
            startDateTime.setHours(schedule.startHour, schedule.startMin, 0, 0);

            const endDateTime = new Date(currentDate);
            endDateTime.setHours(schedule.endHour, schedule.endMin, 0, 0);

            if (startDateTime >= startDate && endDateTime <= endDate) {
                dates.push([startDateTime, endDateTime, schedule.pricings]);
            }
        });

        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
}

export function splitRangeIntoSlots(
    range: [Date, Date],
    slotDurationInMs: number,
    returnAsOneChunk: boolean
) {
    if (returnAsOneChunk) {
        return [range];
    }
    const slots: [Date, Date][] = [];
    let start = new Date(range[0]);
    const end = new Date(range[1]);
    while (start < end) {

        const newTime = new Date(start.getTime() + slotDurationInMs);
        const clampedNewTime = clamp(newTime, { start, end });
        slots.push([start, clampedNewTime]);
        start = new Date(start.getTime() + slotDurationInMs);
    }
    return slots;
}
